/**
 * Deterministic, hydration-safe date display formatting.
 *
 * WHY NOT Intl.DateTimeFormat: output differs between JS engines for the same
 * locale tag — WebKit (Safari/iOS) renders bare 'ar' with different patterns
 * than Node/V8 ("18 يوليو، 2026" vs "18 يوليو 2026", weekday "أحد" vs "الأحد";
 * 'ar-SA' even flips WebKit to the Islamic calendar). Any Intl-formatted text
 * that appears in SSR HTML therefore hydration-mismatches (React #418) on
 * Safari for Arabic visitors. date-fns formats from data bundled in OUR
 * JS bundle, so server and every browser produce byte-identical text.
 *
 * Date-only strings ('yyyy-MM-dd') are parsed with parseISO → LOCAL midnight.
 * `new Date('2026-07-18')` parses as UTC midnight, which renders as Jul 17 in
 * negative-offset timezones (and mismatches SSR); local midnight keeps the
 * calendar day stable in every timezone.
 */

import { format, parseISO, type Locale } from 'date-fns'
import { ar } from 'date-fns/locale'

/** date-fns locale for a booking locale tag; undefined = date-fns' en-US default. */
export function dateFnsLocale(locale: string | undefined): Locale | undefined {
  return locale?.startsWith('ar') ? ar : undefined
}

/** Parse a 'yyyy-MM-dd' (or full ISO) string as a local-time Date; pass Dates through. */
export function toLocalDate(value: string | Date): Date {
  return typeof value === 'string' ? parseISO(value) : value
}

/** "18 يوليو" / "Jul 18" — deterministic across server and all browsers. */
export function formatDayMonth(value: string | Date, locale: string | undefined): string {
  const loc = dateFnsLocale(locale)
  return format(toLocalDate(value), loc ? 'd MMM' : 'MMM d', { locale: loc })
}

/** "18 يوليو 2026" / "Jul 18, 2026" — deterministic across server and all browsers. */
export function formatDayMonthYear(value: string | Date, locale: string | undefined): string {
  const loc = dateFnsLocale(locale)
  return format(toLocalDate(value), loc ? 'd MMM yyyy' : 'MMM d, yyyy', { locale: loc })
}

/**
 * "18 يوليو 2026، 14:32" / "Jul 18, 2026, 14:32" — a TIMESTAMP, not a date.
 *
 * Use this for any `*_at` column. A bare date on an audit field ("updated Jul
 * 18") cannot answer the only question it is ever asked — was this before or
 * after the thing I am looking at — and two edits on one day read as one.
 *
 * 24-hour on purpose: it is unambiguous in both locales and needs no am/pm
 * token that date-fns would render differently per locale data.
 *
 * @param value - ISO timestamp string, or a Date.
 * @param locale - booking locale tag; 'ar…' switches the month names.
 * @returns Empty string for an unparseable value, never "Invalid Date".
 */
export function formatDateTime(value: string | Date, locale: string | undefined): string {
  const d = toLocalDate(value)
  if (Number.isNaN(d.getTime())) return ''
  const loc = dateFnsLocale(locale)
  return format(d, loc ? 'd MMM yyyy، HH:mm' : 'MMM d, yyyy, HH:mm', { locale: loc })
}
